import { APPLICATION_IDS } from "../../../../common/utils/constants";
import {
    CleanroomMetaType,
    EnrichmentInput,
    Table,
} from "../../../common/types/cleanroom";
import { extractDetailsFromFqtnOrFqtc } from "./Utils";
import { THIRD_PARTY_IDENTITY_PROVIDER } from "../../../common/constants/constants";

export enum TAB_TITLE {
    COLLABORATOR = "Collaborator Summary",
    SUMMARY = "My Summary",
    TABLE_RELATIONS = "Table Relations",
    SHARED_WITH = "Shared With",
    MY_TABLE_STATS = "Data Stats",
}

const COLUMN_TYPES = {
    FILTER_AND_GROUP_BY: "FILTER_AND_GROUP_BY",
    AGGREGATE: "AGGREGATE",
    FILTER: "FILTER",
};

// BE Constants
export const ANALYSIS_TITLE = {
    [APPLICATION_IDS.SQL_ANALYSIS]: "SQL Query",
    [APPLICATION_IDS.REACH_AND_FREQUENCY]: "Reach & Frequency",
    [APPLICATION_IDS.LOOKALIKE_MODELING]: "Lookalike Modeling",
    [APPLICATION_IDS.CONVERSION_TRACKING]: "Last-touch Attribution",
    [APPLICATION_IDS.AUDIENCE_OVERLAP]: "Audience Overlap & Segmentation",
    [APPLICATION_IDS.INVENTORY_FORCASTING_PLANNING]: "Inventory Forecasting",
};

// BE Constants
export const COL_SELECTIONS_KEYS = {
    [COLUMN_TYPES.FILTER_AND_GROUP_BY]: "group_by_filter_columns",
    [COLUMN_TYPES.AGGREGATE]: "aggregate_columns",
    [COLUMN_TYPES.FILTER]: "filter_columns",
};

export interface IdentityHubEntry {
    type: THIRD_PARTY_IDENTITY_PROVIDER;
    name: string;
}

// Content for a single enrichment configuration option
export type IdentityHubDetail = IdentityHubEntry & {
    tableDetails: IdentityHubTableDetail[];
};

// Content for a single table in an enrichment configuration
export type IdentityHubTableDetail = {
    table: Table;
    enrichmentInputs: EnrichmentInput[];
};

export enum ColumnType {
    SEGMENTATION_AND_ATTRIBUTE = "Segmentation & Attribute",
    TIMESTAMP = "Timestamp",
    CHANNEL_COLUMN = "Channel",
    CAMPAIGN_ID = "Campaign ID",
    CAMPAIGN_NAME = "Campaign Name",
    UNIQUE_ID_COLUMN = "Unique ID",
    CREATIVE_ID_COLUMN = "Creative ID",
    CLICKS = "Clicks",
    CONVERSIONS = "Conversions",
    VARIANT = "Variant",
    AGGREGATE = "Aggregate",
    APPROVED = "Approved",
    GROUP_BY = "Group By",
    DATE = "Date",
    HOUR = "Hour",
    FILTER = "Filter",
    FILTER_AND_GROUP_BY = "Filter & Group By",
    FULLY_PERMITTED = "Fully permitted",
    FEATURE = "Feature",
    AGGREGATION_POLICY = "Aggregation Policy",
    EMPTY = "",
    PROJECTION_POLICY = "Projection Policy",
    ACTIVATION_ID_COLUMN = "Activation",
    APPROVED_AND_ACTIVATION = "Approved & Activation",
    FILTER_AND_ACTIVATION = "Filter & Activation",
    JOIN_COLUMN = "Join Policy",
    USER_IDENTIFIER = "User Identifier",
}

export const joinColumnKey = "join_columns";

export const applicationColumnTypeMap: Record<string, ColumnType> = {
    filter_columns: ColumnType.FILTER,
    group_by_filter_columns: ColumnType.FILTER_AND_GROUP_BY,
    timestamp_columns: ColumnType.TIMESTAMP,
    day_columns: ColumnType.DATE,
    hour_columns: ColumnType.HOUR,
    channel_columns: ColumnType.CHANNEL_COLUMN,
    activation_id_columns: ColumnType.ACTIVATION_ID_COLUMN,
    campaign_id_columns: ColumnType.CAMPAIGN_ID,
    // "user_identifier_columns": ColumnType., // See if this needs to be rendered
    campaign_name_columns: ColumnType.CAMPAIGN_NAME,
    // "training_columns": ColumnType., See if this needs to be rendered
    aggregate_columns: ColumnType.AGGREGATION_POLICY,
    projection_columns: ColumnType.PROJECTION_POLICY,
    fully_permitted_columns: ColumnType.FULLY_PERMITTED,
    approved_columns: ColumnType.APPROVED,
    unique_id_columns: ColumnType.UNIQUE_ID_COLUMN,
    segmentation_attribute_columns: ColumnType.SEGMENTATION_AND_ATTRIBUTE,
    [joinColumnKey]: ColumnType.JOIN_COLUMN, // We don't want to render a message for join columns in the summary card
    training_columns: ColumnType.FEATURE,
    user_identifier_columns: ColumnType.USER_IDENTIFIER,
};

export const IDENTITY_HUB_TYPES: Record<string, IdentityHubEntry> = {
    [THIRD_PARTY_IDENTITY_PROVIDER.UID_2]: {
        type: THIRD_PARTY_IDENTITY_PROVIDER.UID_2,
        name: "UID 2.0",
    },
    [THIRD_PARTY_IDENTITY_PROVIDER.LIVERAMP_IDENTITY_SOLUTION]: {
        type: THIRD_PARTY_IDENTITY_PROVIDER.LIVERAMP_IDENTITY_SOLUTION,
        name: "LiveRamp",
    },
    [THIRD_PARTY_IDENTITY_PROVIDER.ACXIOM_IDENTITY_GENERATION]: {
        type: THIRD_PARTY_IDENTITY_PROVIDER.ACXIOM_IDENTITY_GENERATION,
        name: "Acxiom Real Identity",
    },
    [THIRD_PARTY_IDENTITY_PROVIDER.ACXIOM_TRANSCODING]: {
        type: THIRD_PARTY_IDENTITY_PROVIDER.ACXIOM_TRANSCODING,
        name: "Acxiom Real Identity Transcoding",
    },
    [THIRD_PARTY_IDENTITY_PROVIDER.TRANSUNION_IDENTITY_GENERATION]: {
        type: THIRD_PARTY_IDENTITY_PROVIDER.TRANSUNION_IDENTITY_GENERATION,
        name: "TransUnion",
    },
    [THIRD_PARTY_IDENTITY_PROVIDER.MERKURY_IDENTITY_GENERATION]: {
        type: THIRD_PARTY_IDENTITY_PROVIDER.MERKURY_IDENTITY_GENERATION,
        name: "Merkury",
    },
    [THIRD_PARTY_IDENTITY_PROVIDER.GOOGLE_PAIR_DV360]: {
        type: THIRD_PARTY_IDENTITY_PROVIDER.GOOGLE_PAIR_DV360,
        name: "DV 360 PAIR",
    },
    [THIRD_PARTY_IDENTITY_PROVIDER.EXPERIAN]: {
        type: THIRD_PARTY_IDENTITY_PROVIDER.EXPERIAN,
        name: "Experian",
    },
};

export const RESOLUTION_TYPE = "Resolution Type";
export const DECONFLICTION_OPTION = "Deconfliction Option";
export const ATTRIBUTES = "Attributes";
export const USER_IDENTIFIERS = "User Identifiers";
export const OUTPUT_TABLE = "Output Table";
export const ID_COLUMNS = "ID Columns";
export const RECORD_COLUMN = "Unique Record Column";
export const PAIR_COLUMNS = "PAIR Join Columns";
export const AWS_SOURCE_PREFIX = "S3_";
export const AZURE_SOURCE_PREFIX = "AZURE_";
export const GCS_SOURCE_PREFIX = "GCS_";
export const ANALYSES_AND_QUERIES = "Analyses & Queries";
export const RAMP_ID_COLUMNS = "Ramp ID Columns";
export const REAL_ID_COLUMNS = "Real ID Columns";
export const COLLABORATOR = "Collaborator";
export const HOUSEHOLD_ID_LABEL = "Household ID";
export const PERSON_ID_LABEL = "Person ID";
export const ADDRESS_ID_LABEL = "Address ID";

export const TABLE_RELATIONS_SECTION_HEADER = "Table Relations";
